import { useCallback, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useRefresh, useVersion } from 'react-admin';
import { Box, Button, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import VisibilityIcon from '@material-ui/icons/Visibility';

import { PermissionsProvider } from 'providers/permissionsProvider';
import { CAN_EDIT_SP_POTENTIAL_MATCHES } from 'providers/permissions';
import { SnackbarContainer } from 'components/standalone';
import { extractErrorMessage } from 'utils';

import { BuilderContainer, BuilderFormValues } from '../containers';
import { BuilderForm, SpMatchesSection } from '../components';

const BuildersEditInner = () => {
  const classes = useStyles();
  const history = useHistory();
  const refresh = useRefresh();
  const { id } = useParams<{ id: string }>();
  const { notify, notifyWithUndo } = SnackbarContainer.useContainer();
  const { hasPermission } = PermissionsProvider.useContainer();

  const canEditSpMatches = hasPermission(CAN_EDIT_SP_POTENTIAL_MATCHES);

  const {
    selectedBuilder,
    isLoading,
    builderLoaded,
    builderError,
    formLoading,
    loadBuilderById,
    updateBuilder,
    loadSpBuilders,
  } = BuilderContainer.useContainer();

  // BACKWARDS COMPAT: useVersion triggers re-fetch when react-admin
  // toolbar refresh button is clicked. Required while using custom
  // containers instead of react-admin's built-in data hooks.
  const version = useVersion();

  const builderId = selectedBuilder?.id;

  useEffect(() => {
    if (!id) return;
    loadBuilderById(id);
  }, [id, version, loadBuilderById]);

  useEffect(() => {
    if (builderError || (builderLoaded && !selectedBuilder)) {
      notify('Element does not exist', 'error');
      history.push('/builder');
    }
  }, [builderError, builderLoaded, selectedBuilder, notify, history]);

  // The SP-matches section is deliberately gated on
  // CAN_EDIT_SP_POTENTIAL_MATCHES only — asymmetric with the Show page's
  // two-tier SEE/EDIT gating.
  useEffect(() => {
    if (!builderId || !canEditSpMatches) return;
    loadSpBuilders(builderId).catch(() => {
      notify('Failed to load SupplyPro builder names.', 'error');
    });
  }, [builderId, canEditSpMatches, loadSpBuilders, notify]);

  const handleSubmit = useCallback(
    async (values: BuilderFormValues) => {
      if (!id || !selectedBuilder) return;

      notifyWithUndo({
        message: 'Element updated',
        optimistic: {
          resource: 'builder',
          id,
          data: values,
        },
        onCommit: async () => {
          try {
            await updateBuilder(id, values);
          } catch (error: unknown) {
            console.error('Builder update failed:', error);
            notify(
              extractErrorMessage(error, 'Failed to update builder'),
              'error',
            );
            history.push(`/builder/${id}`);
          } finally {
            refresh();
          }
        },
        onCancel: () => {
          notify('Action cancelled');
        },
      });
      history.push('/builder');
    },
    [
      id,
      selectedBuilder,
      updateBuilder,
      history,
      refresh,
      notify,
      notifyWithUndo,
    ],
  );

  const handleGoBack = useCallback(() => {
    history.push('/builder');
  }, [history]);

  const handleShow = useCallback(() => {
    if (!id) return;
    history.push(`/builder/${id}/show`);
  }, [history, id]);

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedBuilder) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.topToolbar}>
        <Button
          color="primary"
          startIcon={<VisibilityIcon />}
          onClick={handleShow}
          size="small"
        >
          Show
        </Button>
      </Box>

      <Box className={classes.formContainer}>
        <BuilderForm
          mode="edit"
          initialValues={selectedBuilder}
          onSubmit={handleSubmit}
          loading={formLoading}
          onGoBack={handleGoBack}
        />
      </Box>

      {canEditSpMatches && <SpMatchesSection canEdit={canEditSpMatches} />}
    </Box>
  );
};

export const BuildersEdit = () => (
  <BuilderContainer.Provider>
    <BuildersEditInner />
  </BuilderContainer.Provider>
);

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: '1500px',
  },
  topToolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    minHeight: 'auto',
  },
  formContainer: {
    position: 'relative',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
}));

export default BuildersEdit;
